import { useQuery } from '@apollo/client/react'
import {
  Badge,
  Button,
  Center,
  Heading,
  HStack,
  Icon,
  LinkBox,
  SimpleGrid,
  SimpleGridProps,
  Spinner,
  StackProps,
  Text,
  VStack,
} from '@chakra-ui/react'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { ScoUserSearch } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { gql } from '@src/lib/gql'
import React from 'react'
import { LuChevronRight, LuSearchCheck } from 'react-icons/lu'

export type UserSearchesListProps = {
  stackProps?: StackProps
  resultsSimpleGridProps?: SimpleGridProps
  hideIfEmpty?: boolean
  hideTitle?: boolean
  renderEmpty?: () => any
  excludeEmptyResults?: boolean
}

export const UserSearchesList = (props: UserSearchesListProps) => {
  const { currentUser } = useCurrentUser()
  const query = useQuery(
    gql`
      query ($searchingUserId: String!, $excludeEmptyResults: Boolean) {
        scoUserSearches(searchingUserId: $searchingUserId, excludeEmptyResults: $excludeEmptyResults) {
          id
          items {
            id
            userSearchId
            title
            naturalLanguagePrompt
            processingStatus
            userMatches {
              id
              pagination {
                itemCount
              }
            }
          }
        }
      }
    `,
    {
      fetchPolicy: 'network-only',
      ssr: false,
      skip: !currentUser?.userId,
      variables: {
        excludeEmptyResults: props.excludeEmptyResults,
        searchingUserId: currentUser?.userId,
      },
    }
  )
  const userSearches: ScoUserSearch[] = query.data?.scoUserSearches?.items || []

  if (!currentUser?.isRegistered) {
    return <></>
  }

  if (props.hideIfEmpty && (query.loading || userSearches?.length === 0)) {
    return <></>
  }

  if (props.renderEmpty && userSearches?.length === 0) {
    return <>{props.renderEmpty()}</>
  }

  return (
    <VStack align={'stretch'} height={'full'} {...props.stackProps}>
      {!props.hideTitle && (
        <HStack paddingY={2}>
          <Icon asChild>
            <LuSearchCheck />
          </Icon>
          <Heading size={'sm'}>Your Searches</Heading>
        </HStack>
      )}
      {query.loading && (
        <Center>
          <Spinner />
        </Center>
      )}
      <VStack align={'stretch'} gap={2} height={'full'}>
        {userSearches?.length === 0 && (
          <VStack bgColor={'colorPalette.subtle/50'} borderRadius={'md'} padding={4}>
            <Text>No searches yet.</Text>
            <Button asChild variant={'outlineGhost'} width={'full'}>
              <NextLink href={`/members/matching/start`}>
                Get Started
                <LuChevronRight />
              </NextLink>
            </Button>
          </VStack>
        )}
        <SimpleGrid columns={1} gap={2} {...props.resultsSimpleGridProps} maxHeight={'100%'}>
          {userSearches?.map(userSearch => (
            <LinkBox
              key={userSearch.id}
              asChild
              _hover={{ bgColor: 'colorPalette.container/70' }}
              bgColor={'colorPalette.container'}
              borderRadius={'md'}
              borderTop={'1px solid'}
              borderTopColor={'colorPalette.border'}
              boxShadow={'xs'}
              color={'colorPalette.fg'}
              padding={4}
            >
              <NextLink key={userSearch.id} href={`/members/matching/${userSearch.userSearchId}`}>
                <VStack align={'flex-start'} width={'full'}>
                  <Text fontWeight={'semibold'} textStyle={'xs'}>
                    {userSearch.title}
                  </Text>
                  <Badge marginTop={'2'} paddingX={'2'} paddingY={'1'} size={'xs'} variant={'solid'}>
                    {userSearch.userMatches.pagination.itemCount} Matches
                  </Badge>
                </VStack>
              </NextLink>
            </LinkBox>
          ))}
        </SimpleGrid>
      </VStack>
    </VStack>
  )
}
